import { useStripe } from "@stripe/react-stripe-js";
import React, { useEffect, useMemo, useState } from "react";

import { useUserContext } from "../userDashboard/contexts/userContext";

import credit from "./assets/Credit_Card.svg";
import load from "./assets/load.gif";
import { buildStripeReturnUrl } from "./stripeReturnUrl";

export interface Card {
    id: string | number;
    stripe_payment_method_id: string;
    brand: string;
    last4: string;
    currency?: string;
    currency_code?: string;
    country?: string | { currency?: string;[key: string]: unknown };
    country_code?: string;
    countryCode?: string;
    [key: string]: unknown;
}

interface SavedCardsProps {
    savedCards: Card[];
    clientSecret: string;
    setModal: (isOpen: boolean) => void;
    payment_method: string;
    transactionID: string;
    reset?: () => void;
    onPaymentSuccess?: (result?: unknown) => void;
    onPaymentError?: (error: unknown) => void;
    triggerFetch?: () => void;
    currencyFilter?: string | undefined;
    countryCodeFilter?: string | undefined;
}

const SavedCards: React.FC<SavedCardsProps> = ({
    savedCards,
    clientSecret,
    setModal,
    payment_method,
    transactionID,
    reset,
    onPaymentSuccess = () => { },
    onPaymentError = () => { },
    triggerFetch = () => { },
    currencyFilter,
    countryCodeFilter,
}) => {
    const stripe = useStripe();
    const [loading, setLoading] = useState<boolean>(false);
    const [paymentMethodID, setPaymentMethodID] = useState<string>(""); // state to store the selected payment method ID
    const { refreshUserProfile } = useUserContext();
    const [currentPage, setCurrentPage] = useState<number>(1);
    const cardsPerPage = 2;

    const normalize = (value: unknown) =>
        typeof value === "string" && value.trim().length > 0
            ? value.trim().toUpperCase()
            : "";

    const filteredCards = useMemo(() => {
        const expectedCurrency = normalize(currencyFilter);
        const expectedCountry = normalize(countryCodeFilter);

        if (!expectedCurrency && !expectedCountry) {
            return savedCards;
        }

        return savedCards.filter((card) => {
            const cardCurrency =
                normalize(card.currency) ||
                normalize(card.currency_code) ||
                normalize(typeof card.country === "object" ? card.country?.currency : undefined);
            const cardCountry =
                normalize(card.country_code) ||
                normalize(card.countryCode) ||
                normalize(card.country);

            const currencyOk = !expectedCurrency || cardCurrency === expectedCurrency;
            const countryOk = !expectedCountry || cardCountry === expectedCountry;

            return currencyOk && countryOk;
        });
    }, [savedCards, currencyFilter, countryCodeFilter]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredCards.length]);

    const totalPages = Math.ceil(filteredCards.length / cardsPerPage) || 1;
    const startIndex = (currentPage - 1) * cardsPerPage;
    const endIndex = startIndex + cardsPerPage;
    const currentCards = filteredCards.slice(startIndex, endIndex);

    const onSuccess = (response: unknown) => {
        setLoading(false);
        reset?.();
        onPaymentSuccess?.(response);
        refreshUserProfile();
        setModal(false);
        triggerFetch();
    };

    const onError = (error: unknown) => {
        setLoading(false);
        onPaymentError(error);
    };

    const handlePay = async (paymentMethodId: string) => {
        setPaymentMethodID(paymentMethodId);
        setLoading(true);

        if (!stripe) {
            onError(new Error("Secure card payment is still loading. Please try again."));
            return;
        }

        try {
            const { error, paymentIntent } = await stripe.confirmCardPayment(
                clientSecret,
                {
                    payment_method: paymentMethodId,
                    return_url: buildStripeReturnUrl(transactionID, payment_method),
                }
            );

            if (error) {
                onError(error);
                return;
            }

            if (!paymentIntent) {
                onError(new Error("Stripe did not return a payment status."));
                return;
            }

            if (paymentIntent.status === "succeeded" || paymentIntent.status === "processing") {
                onSuccess({ payment_intent: paymentIntent });
                return;
            }

            if (paymentIntent.status === "requires_action") {
                onError(new Error("Bank verification was not completed. Please try again."));
                return;
            }

            onError(new Error(
                paymentIntent.status === "requires_payment_method"
                    ? "This card payment was not completed. Try another card."
                    : "This payment is not complete yet."
            ));
        } catch (error: unknown) {
            onError(error);
        }
    };

    return (
        <div className="mt-4">
            <p className="font-Manrope font-bold text-lg md:text-xl text-[#272D37]">
                Select a Saved Card
            </p>

            {filteredCards.length > 0 ? (
                <>
                    {currentCards.map((card) => (
                        <div
                            key={card.id}
                            className={`mt-6 w-full border rounded-2xl p-3 flex items-center justify-between ${paymentMethodID === card.stripe_payment_method_id
                                ? "border-[#317333]"
                                : "border-[#EAEBF0]"
                                }`}
                        >
                            <div className="flex items-center space-x-3">
                                <img src={credit} alt="Credit Card" />
                                <div>
                                    <p className="text-sm md:text-base text-[#272D37] font-semibold font-Manrope">
                                        <span className="capitalize">{card.brand}</span> ending in{" "}
                                        {card.last4}
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => handlePay(card.stripe_payment_method_id)}
                                disabled={
                                    loading && paymentMethodID === card.stripe_payment_method_id
                                }
                                className="bg-[#317333] text-white px-4 py-2 rounded-lg text-sm font-Manrope font-semibold"
                            >
                                {loading &&
                                    paymentMethodID === card.stripe_payment_method_id ? (
                                    <img src={load} alt="Loading" className="w-5" />
                                ) : (
                                    "Pay"
                                )}
                            </button>
                        </div>
                    ))}

                    {/* Pagination controls */}
                    {filteredCards.length > cardsPerPage && (
                        <div className="flex justify-center items-center gap-4 mt-6">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-2 py-1 rounded-md border text-xs font-Manrope font-medium disabled:opacity-50"
                            >
                                Prev
                            </button>
                            <span className="text-xs font-Manrope text-[#272D37] mt-1">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                onClick={() =>
                                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                                }
                                disabled={currentPage === totalPages}
                                className="px-2 py-1 rounded-md border text-xs font-Manrope font-medium disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <p className="mt-4 text-[#272D37] font-Manrope text-sm">
                    {savedCards.length === 0
                        ? "No saved cards available. Please add a card to proceed with payment."
                        : "No saved cards match the required currency. Please add a new card."}
                </p>
            )}
        </div>
    );
};

export default SavedCards;
